import React from 'react';
import { WithRouterProps } from '../util/withRouter';
import { Server } from '../../server/server';
import MessageModal from '../modals/MessageModal';
import AlertModal from '../modals/AlertModal';
import PageEnvelope from '../elements/PageEnvelope';
import ContentPanel from '../elements/ContentPanel';
import ActivityButtons from '../elements/ActivityButtons';
import { Navigate } from 'react-router-dom';
import PostPanel from '../elements/PostPanel';
import './SitePage.css';
import InfoPanel from '../elements/InfoPanel';
import { getDeviceType } from '../util/util';
import { BsActivity } from 'react-icons/bs';
import TabPanel from '../elements/TabPanel';

const StateCache = new Map();

interface ActivityPageProps {
  router: WithRouterProps;
}

interface ActivityPageState {
  posts: any[];
  message: string;
  alert: string;
  category: string;
  hasNewPosts: boolean;
  loadingPosts: boolean;
  loadingMore: boolean;
  hideMore: boolean;
  navigate: string;
  maxPosts: number;
  noMorePosts: boolean;
  newActivity: boolean;
  loadingNewActivity: boolean;
  infoPanel: string;
  tab: string;
}

class ActivityPage extends React.Component<ActivityPageProps, ActivityPageState> {
  private activityMonitorInterval: number | null = null;
  private activityMonitorSchedule: number | null = null;
  private loggingOut: boolean = false;
  private tabs:any[];

  constructor(props: ActivityPageProps) {
    super(props);
    
    this.state = {
      posts: [],
      message: '',
      alert: '',
      category: 'community',
      hasNewPosts: false,
      loadingPosts: false,
      loadingMore: false,
      hideMore: false,
      navigate: '',
      maxPosts: 10,
      noMorePosts: false,
      newActivity: false,
      loadingNewActivity: false,
      infoPanel: '',
      tab: 'community'
    };

    this.tabs = [
      {
        id: 'community', 
        name: 'Community',
      },
      {
        id: 'friends', 
        name: 'Friends'
      },
      {
        id: 'following', 
        name: 'Following'
      }
    ];

    this.onLogout = this.onLogout.bind(this);
  }

  componentDidMount() {
    Server.account.addEventListener('logout', this.onLogout);

    const cachedState = StateCache.get(this.props.router.location.key);
    if (cachedState) 
      this.setState(cachedState);
    StateCache.clear();

    if(localStorage.getItem('ActivityPageInstallHint') == null)
      this.setState({infoPanel: 'install'});
    else if(localStorage.getItem('ActivityPageNotificationsHint') == null)
      this.setState({infoPanel: 'notifications'});

    // Initialize newActivity state
    // this.setState({ newActivity: Server.monitor.isNewActivity() });

    this.load();
  }

  componentWillUnmount(): void {
    Server.account.removeEventListener('logout', this.onLogout);
    
    if(!this.loggingOut) {
      let state = {...this.state, navigate: ''};
      StateCache.set(this.props.router.location.key, state);
    }
    else 
      this.loggingOut = false;

    this.stopActivityMonitoring();
  }

  onLogout() {
    StateCache.clear();
    this.loggingOut = true;
  }

  startActivityMonitoring() {
    this.activityMonitorInterval = setInterval(() => {
      const currentNewActivity = Server.monitor.isNewActivity();
      if (currentNewActivity !== this.state.newActivity) 
        this.setState({ newActivity: currentNewActivity });
    }, 250); // Check every second
  }

  stopActivityMonitoring() {
    if(this.activityMonitorSchedule) {
      clearTimeout(this.activityMonitorSchedule);
      this.activityMonitorSchedule = null;
    }

    if (this.activityMonitorInterval) {
      clearInterval(this.activityMonitorInterval);
      this.activityMonitorInterval = null;
    }
  }

  async load() {
    await this.loadPosts();
    this.activityMonitorSchedule = setTimeout(() => {
      this.startActivityMonitoring();
    }, 1000);
  }

  async loadPosts() {
    let cachedPosts = await Server.activity.getPostsCache();

    let updating = false;
    if(cachedPosts) {
      let profiles = this.getProfilesInPosts(cachedPosts);
      profiles = profiles.filter((profile:any)=>Server.public.getProfile(profile) == null);
      if(profiles.length > 0) 
        await Server.public.loadProfiles(profiles);

      this.setState({posts: cachedPosts, noMorePosts: cachedPosts.length < 10});
      
      if(cachedPosts.length > 0) 
        Server.monitor.setActivityLastRead(cachedPosts[0].date)

      updating = cachedPosts.length > 0;
      let postRefreshTime = window.sessionStorage.getItem(`ActivityPostsRefresh`);
      if(postRefreshTime) {
        let refreshTime = parseInt(postRefreshTime);
        let now = new Date().getTime();
        if(now - refreshTime < 1000 * 60) 
          return;
      }
    }

    let params:any = {limit: 10};

    if(updating)
      params.after = {date: cachedPosts[0].date, id: cachedPosts[0].id};
    else
      this.setState({loadingPosts: true});

    let response = await Server.activity.getPosts(params);

    if(!updating)
      await Server.monitor.sleep(250);

    if(!response.success) {
      this.setState({alert: response.message, loadingPosts: false});
      return;
    }

    window.sessionStorage.setItem(`ActivityPostsRefresh`, new Date().getTime().toString());
    
    let newPosts = response.posts;

    if(newPosts.length == 0 && updating) {
      this.setState({loadingPosts: false});
      return;
    }

    let profiles = this.getProfilesInPosts(newPosts);
    await Server.public.loadProfiles(profiles);

    // building new list of topics
    let posts = [];
    if(updating && newPosts.length < 10)
      posts = [...cachedPosts];
    
    posts = this.addNewPostsToExistingPosts(newPosts, posts);

    await Server.activity.setPostsCache(posts, updating);

    if(!updating) {
      let noMorePosts = posts.length < 10;
      this.setState({noMorePosts});
    }

    this.setState({posts, loadingPosts: false, loadingNewActivity: false, newActivity: false});

    if(posts.length > 0) 
      await Server.monitor.setActivityLastRead(posts[0].date)
  }

  async loadMorePosts() {
    this.setState({loadingMore: true});

    let params:any = {limit: 10};

    let post = this.state.posts[this.state.posts.length-1];
    params.before = {date: post.date, id: post.id};

    let response = await Server.activity.getPosts(params);

    if(!response.success) {
      this.setState({loadingMore: false, alert: response.message});
      return;
    }

    let newPosts = response.posts;

    if(newPosts.length == 0) {
      this.setState({loadingMore: false, noMorePosts: true});
      return;
    }

    let profiles = this.getProfilesInPosts(newPosts);
    await Server.public.loadProfiles(profiles);

    let posts = this.addNewPostsToExistingPosts(newPosts, this.state.posts);
    
    await Server.activity.setPostsCache(posts, true);

    let noMorePosts = posts.length < 10;

    this.setState({posts, noMorePosts, loadingMore: false});
  }

  addNewPostsToExistingPosts(newPosts:any[], existingPosts:any[]) {
    let posts = [...existingPosts];

    for (let newPost of newPosts) {
      let existingIndex = posts.findIndex(post => 
        post.author === newPost.author && post.date === newPost.date
      );
      
      if (existingIndex !== -1) 
        posts[existingIndex] = newPost;
      else
        posts.push(newPost);
    }

    this.sortPosts(posts);

    return posts;
  }

  sortPosts(posts:any[]) {
    posts.sort((a:any, b:any)=>{
      if(a.date > b.date)
        return -1;
      else if(a.date < b.date)
        return 1;
      return 0;
    })
  }

  getProfilesInPosts(posts:any[]) {
    let profiles = [];
    for (let i = 0; i < posts.length; i++) {
      if(profiles.indexOf(posts[i].author) == -1)
        profiles.push(posts[i].author);
    }
    return profiles;
  }

  async onPost(content:string) {
    this.setState({message: 'Posting...'});
    this.stopActivityMonitoring();

    let response = await Server.activity.createPost(content);

    if (response.success) {
      await Server.activity.clearPostsCache();
      await this.load();
      this.setState({message: ''});
    }
    else
      this.setState({message: '', alert: response.message})
  }

  async onLoadMore() {
    this.loadMorePosts();
  }

  onPostClick(id:string) {
    this.setState({navigate: "/activity/post/" + id});    
  }

  async onShowNewPosts() {
    this.stopActivityMonitoring();
    this.setState({loadingNewActivity: true});
    await this.load();
    window.scrollTo(0, 0);
  }

  onTabChange(id:string) {
    this.setState({tab: id, category: id});
  }

  renderPosts() {
    let lastRead = Server.monitor.getActivityLastRead(); 

    let divs = [];
    for (let i = 0; i < this.state.posts.length; i++) {
      let post = this.state.posts[i];

      if(this.state.category == 'friends' && !Server.user.isFriend(post.author))
        continue;

      if(this.state.category == 'following' && !Server.user.isFollowing(post.author))
        continue;

      let read = true;
      if(lastRead) 
        read = post.date <= lastRead;

      if(!read)
        if(Server.account.isLoggedIn() && post.author == Server.user.getId())
          read = true;

      divs.push(
        <ContentPanel 
          key={i}
          author={post.author}
          date={post.date}
          content={post.content}
          read={read}
          reply={true}
          summary={true}
          onClick={()=>this.onPostClick(post.id)}
        >
          <ActivityButtons 
            likes={post.likes} 
            liked={post.liked} 
            post={post} 
            reply={true} 
            onUpdate={()=>this.forceUpdate()} 
          />
        </ContentPanel>
      )
    }

    let lastDate = null;
    if(this.state.posts.length > 0)
      lastDate = new Date(this.state.posts[this.state.posts.length-1].date).toLocaleString();

    if(divs.length == 0) {
      if(this.state.loadingPosts)
        divs.push(<div key={0} className="site-page-panel">Loading...</div>);
      else if(this.state.category != 'community' && lastDate)
        divs.push(<div key={0} className="site-page-panel">No posts since {lastDate}.</div>);
      else
        divs.push(<div key={0} className="site-page-panel">No posts yet.</div>);
    }
    else {
      if(this.state.category != 'community' && lastDate)
        divs.push(<div key={this.state.posts.length} className="site-page-panel">Posts since {lastDate}.</div>);
    }

    return divs;
  }

  render() {
    if(this.state.navigate != '') 
      return <Navigate to={this.state.navigate} />;

    let installMessage = getDeviceType() == 'desktop' ? 'on your desktop' : 'on your home screen';

    return (
      <div className="site-page">
        <PageEnvelope width="600px">
          <div className="site-page-column">

          <PostPanel 
              placeholder="Whats happening?"
              onPost={(content:string)=>this.onPost(content)}
              maxLength={1024}
            />

          {this.state.infoPanel == 'install' &&
            <InfoPanel 
              item="ActivityPageInstallHint" 
              message={`Did you know you can INSTALL this site ${installMessage}?  Head over to the Settings page! 👉`} 
              onClick={()=>this.setState({navigate: '/settings'})} 
            />
          }
          {this.state.infoPanel == 'notifications' &&
            <InfoPanel 
              item="ActivityPageNotificationsHint" 
              message="Did you know you can enable NOTIFICATIONS when new activity is posted?  Head over to the Settings page! 👉" 
              onClick={()=>this.setState({navigate: '/settings'})} 
            />
          }

          {Server.account.isLoggedIn() &&
            <TabPanel tabs={this.tabs} tab={this.state.tab} onChange={(id:string)=>this.onTabChange(id)} />
          }

          {this.state.newActivity &&
            <div style={{position: 'sticky', top: '70px', zIndex: '19', cursor: 'pointer'}}>
              <div className="site-page-notification-panel" onClick={()=>this.onShowNewPosts()}>
                {this.state.loadingNewActivity ? 'Loading...' : 'Tap here to show new posts.'}
              </div>
            </div>
          }            
 
          {this.renderPosts()}

          {this.state.posts.length > 0 && !this.state.hideMore &&
            <div>
              <button 
                onClick={()=>this.onLoadMore()} 
                disabled={this.state.loadingMore}
              >
                {this.state.loadingMore ? 'Loading...' : 'Load More'}
              </button>
            </div>
          }
        
          </div>

        </PageEnvelope>

        <MessageModal message={this.state.message} />
        <AlertModal message={this.state.alert} button="OK" onClose={()=>this.setState({alert: ''})}/>
      </div>
    );
  }
}

export default ActivityPage;